import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Loader2,
  Rss,
  Globe,
  Search,
  AlertTriangle,
  CheckCircle2,
  CircleSlash,
  ChevronDown,
  ChevronRight,
  ExternalLink,
} from "lucide-react";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

/**
 * Per-run fetch diagnostics. Shows every source that was queried during
 * a research run, with status pill, headline count, and — for anything
 * that errored — the exact Meta / feedparser / Firecrawl message we
 * caught. Editors use this to spot silently-dead outlets (e.g. an RSS
 * feed URL that started 404-ing after a redesign) without having to
 * grep server logs.
 *
 * Data comes from GET /research/{id}/source-report, populated when the
 * research run completes. Older runs pre-dating the feature return an
 * empty array — we surface that as an explanatory empty state rather
 * than an error.
 */
export default function SourceReportDialog({ researchId, open, onOpenChange }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["source-report", researchId],
    queryFn: () => api.getResearchSourceReport(researchId),
    enabled: open && !!researchId,
    // Report is immutable once the run is ready — no need to refetch
    // on window focus or mount.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="source-report-dialog"
        className="max-w-3xl max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">
            Fetch report
          </DialogTitle>
          <DialogDescription className="text-sm text-[var(--ink-muted)]">
            Every source we queried for this run, with headline counts
            and error details for anything that failed.
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="py-10 flex items-center gap-2 text-[var(--ink-muted)]">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading report…
          </div>
        )}

        {isError && (
          <div className="py-6 text-sm text-red-700 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              Couldn&apos;t load report:{" "}
              {error?.response?.data?.detail || error?.message || "unknown error"}
            </span>
          </div>
        )}

        {data && <ReportBody report={data} />}
      </DialogContent>
    </Dialog>
  );
}

function ReportBody({ report }) {
  const { summary = {}, sources = [] } = report || {};
  if (!sources.length) {
    return (
      <div className="py-8 text-sm text-[var(--ink-muted)]">
        No fetch report is available for this run. Runs created before this
        diagnostic panel was added don&apos;t have a saved report — start a new
        research run to see one.
      </div>
    );
  }

  const errored = sources.filter((s) => s.status === "error");
  const empty = sources.filter((s) => s.status === "empty");
  const skipped = sources.filter((s) => s.status === "skipped");
  const ok = sources.filter((s) => s.status === "ok");

  return (
    <div className="space-y-6" data-testid="source-report-body">
      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs font-mono-display">
        <SummaryPill
          label="Sources"
          value={summary.sources_total}
          testId="summary-total"
        />
        <SummaryPill
          label="OK"
          value={summary.sources_ok}
          tone="ok"
          testId="summary-ok"
        />
        <SummaryPill
          label="Empty"
          value={summary.sources_empty}
          tone="warn"
          testId="summary-empty"
        />
        <SummaryPill
          label="Errored"
          value={summary.sources_errored}
          tone="error"
          testId="summary-errored"
        />
      </div>
      <div className="text-xs font-mono-display text-[var(--ink-muted)]">
        Total headlines fetched (before dedupe):{" "}
        <span className="text-[var(--ink)] font-semibold">
          {summary.headlines_fetched ?? 0}
        </span>
      </div>

      {/* Errors first — that's what editors need to act on */}
      {errored.length > 0 && (
        <Section title="Errored" tone="error">
          {errored.map((s, i) => (
            <SourceRow key={`e-${i}`} entry={s} />
          ))}
        </Section>
      )}
      {skipped.length > 0 && (
        <Section title="Skipped (needs configuration)" tone="warn">
          {skipped.map((s, i) => (
            <SourceRow key={`s-${i}`} entry={s} />
          ))}
        </Section>
      )}
      {empty.length > 0 && (
        <Section title="Empty (fetched OK but 0 items)" tone="warn">
          {empty.map((s, i) => (
            <SourceRow key={`n-${i}`} entry={s} />
          ))}
        </Section>
      )}
      {ok.length > 0 && (
        <Section title="Fetched" tone="ok">
          {ok.map((s, i) => (
            <SourceRow key={`o-${i}`} entry={s} />
          ))}
        </Section>
      )}
    </div>
  );
}

function SummaryPill({ label, value, tone = "neutral", testId }) {
  const toneClass =
    tone === "ok"
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : tone === "warn"
        ? "bg-amber-50 text-amber-800 border-amber-200"
        : tone === "error"
          ? "bg-red-50 text-red-800 border-red-200"
          : "bg-[var(--secondary)] text-[var(--ink)] border-[var(--border)]";
  return (
    <div
      data-testid={testId}
      className={`border rounded px-3 py-2 flex items-center justify-between ${toneClass}`}
    >
      <span className="uppercase tracking-wider text-[10px]">{label}</span>
      <span className="text-sm font-semibold">{value ?? 0}</span>
    </div>
  );
}

function Section({ title, tone, children }) {
  const dotClass =
    tone === "ok"
      ? "bg-emerald-500"
      : tone === "warn"
        ? "bg-amber-500"
        : "bg-red-500";
  return (
    <div>
      <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-wider font-mono-display text-[var(--ink-muted)]">
        <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`} />
        {title}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function typeIcon(t) {
  if (t === "rss") return <Rss className="w-3.5 h-3.5" />;
  if (t === "google_news") return <Search className="w-3.5 h-3.5" />;
  if (t === "site_filter") return <Search className="w-3.5 h-3.5" />;
  return <Globe className="w-3.5 h-3.5" />;
}

function statusBadge(status) {
  if (status === "ok")
    return (
      <span className="inline-flex items-center gap-1 text-emerald-700 text-[10px] font-mono-display uppercase">
        <CheckCircle2 className="w-3 h-3" /> ok
      </span>
    );
  if (status === "empty")
    return (
      <span className="inline-flex items-center gap-1 text-amber-700 text-[10px] font-mono-display uppercase">
        <CircleSlash className="w-3 h-3" /> empty
      </span>
    );
  if (status === "skipped")
    return (
      <span className="inline-flex items-center gap-1 text-slate-600 text-[10px] font-mono-display uppercase">
        <CircleSlash className="w-3 h-3" /> skipped
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-red-700 text-[10px] font-mono-display uppercase">
      <AlertTriangle className="w-3 h-3" /> error
    </span>
  );
}

function SourceRow({ entry }) {
  const [open, setOpen] = useState(false);
  const canExpand =
    (entry.headlines && entry.headlines.length > 0) || !!entry.error;
  return (
    <div
      className="border border-[var(--border)] rounded overflow-hidden"
      data-testid="source-row"
    >
      <button
        type="button"
        onClick={() => canExpand && setOpen((v) => !v)}
        className={`w-full flex items-start gap-3 px-3 py-2.5 text-left ${
          canExpand ? "hover:bg-[var(--secondary)]" : "cursor-default"
        }`}
      >
        <span className="text-[var(--ink-muted)] mt-0.5">
          {typeIcon(entry.type)}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-[var(--ink)] truncate">
              {entry.name || "(unnamed)"}
            </span>
            {statusBadge(entry.status)}
            <span className="text-[10px] font-mono-display text-[var(--ink-muted)]">
              {entry.count ?? 0} items · {entry.elapsed_ms ?? 0}ms
            </span>
          </div>
          <div className="text-[11px] font-mono-display text-[var(--ink-muted)] truncate mt-0.5">
            {entry.target}
          </div>
        </div>
        {canExpand && (
          <span className="text-[var(--ink-muted)] mt-0.5">
            {open ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </span>
        )}
      </button>

      {open && (
        <div className="border-t border-[var(--border)] bg-[var(--secondary)] px-3 py-2.5 text-xs">
          {entry.error && (
            <div
              className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-red-900 font-mono-display whitespace-pre-wrap break-words"
              data-testid="source-error"
            >
              {entry.error}
            </div>
          )}
          {entry.headlines && entry.headlines.length > 0 && (
            <ul className="space-y-1.5">
              {entry.headlines.map((h, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-[var(--ink-muted)] font-mono-display shrink-0">
                    {String(i + 1).padStart(2, "0")}.
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[var(--ink)] leading-snug">
                      {h.title || "(untitled)"}
                    </div>
                    {h.url && (
                      <a
                        href={h.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[10px] font-mono-display text-[var(--accent)] hover:underline"
                      >
                        {h.url.length > 80
                          ? h.url.slice(0, 80) + "…"
                          : h.url}
                        <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
